import WebSocketManager from './websocket-manager';
import type { WebSocketManagerConfig } from './types';
import { api } from '../../out/trades';
// import { NR_EVENT_NAMES } from '../constants/nr-events';
import { WebsocketClientType } from '../constants/common';

// ============================================
// Types
// ============================================

export enum TradeEventTypes {
  ORDER = 'order',
  POSITION = 'position',
  GTT_ORDER = 'gtt_order',
  POSITION_REFRESH = 'position_refresh',
}

export enum TradeEventMode {
  ORDER = 'O',
  POSITION = 'PO',
  GTT_ORDER = 'G',
  POSITION_REFRESH = 'PO_REF',
}

export interface TradeData {
  subPath?: string;
  mode?: string;
  [key: string]: unknown;
}

export type TradesCallback = (
  message: TradeData | null,
  error?: unknown,
) => void;

interface ISubscriptions {
  path: string;
  callback: TradesCallback;
  subId: string;
  type: TradeEventTypes;
}

type TWebsocketPath = string;
type TSubscriptionKey = `${TWebsocketPath}-${TradeEventMode}`;

// Default protobuf decoder for trades
const defaultProtobufDecoder = (data: Uint8Array): TradeData => {
  const TradeDataClass = api.trades.TradeData;
  return TradeDataClass.decode(data) as unknown as TradeData;
};

export interface TradesWebSocketManagerConfig {
  websocketUrl: string;
  socketId: WebsocketClientType;
  protobufDecoder?: (data: Uint8Array) => TradeData;
  websocketConfig: WebSocketManagerConfig;
}

// ============================================
// TradesWebSocketManager Class
// ============================================

class TradesWebSocketManager extends WebSocketManager {
  private static tradesInstance: TradesWebSocketManager | null = null;
  private tradesWebSocketUrl: string;
  private static setConnectionCallback: (
    ws: WebSocket | null,
    client: string,
  ) => void = () => {};
  private socketId: WebsocketClientType = WebsocketClientType.TRADES;
  private subscriptions: Map<TSubscriptionKey, ISubscriptions[]> = new Map();
  private connectionStartTime: number = 0;
  private isConnectionInProgress: boolean = false;
  private lastOnMessageError: string = '';
  private protobufDecoder: (data: Uint8Array) => TradeData;

  private constructor(config: TradesWebSocketManagerConfig) {
    super(config.websocketConfig);
    this.tradesWebSocketUrl = config.websocketUrl;
    this.socketId = config.socketId;
    this.protobufDecoder = config.protobufDecoder || defaultProtobufDecoder;
  }

  static getTradesInstance(
    config?: TradesWebSocketManagerConfig,
  ): TradesWebSocketManager {
    if (!this.tradesInstance) {
      if (!config) {
        throw new Error('Config is required for first initialization');
      }
      this.tradesInstance = new TradesWebSocketManager(config);
    }
    return this.tradesInstance;
  }

  static initializeTradesSocketManager(
    config: TradesWebSocketManagerConfig,
    setConnectionCallback: (ws: WebSocket | null, client: string) => void,
    hashToken: string | null = null,
  ): void {
    console.log('[TradesWebSocketManager] Initializing socket manager', {
      socketId: config.socketId,
      hasHashToken: !!hashToken,
    });
    if (!this.tradesInstance) {
      this.tradesInstance = new TradesWebSocketManager(config);
    }
    super.initialize(hashToken);
    TradesWebSocketManager.setConnectionCallback = setConnectionCallback;
  }

  static getTradesWSInstance(): TradesWebSocketManager | null {
    if (!this.tradesInstance) {
      return null;
    }
    return this.tradesInstance;
  }

  static resetInstance(): void {
    this.tradesInstance = null;
  }

  async initializeTradesConnection(): Promise<void> {
    try {
      if (this.isConnectionInProgress) {
        console.log('[TradesWebSocketManager] Connection already in progress');
        return;
      }
      this.isConnectionInProgress = true;
      console.log('[TradesWebSocketManager] Initializing connection', {
        socketId: this.socketId,
        url: this.tradesWebSocketUrl,
      });
      await super.initializeConnection(
        this.socketId,
        this.tradesWebSocketUrl,
        this.setupEventListeners.bind(this),
      );
      this.connectionStartTime = Date.now();
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error initializing trades connection:',
        error,
      );
    } finally {
      this.isConnectionInProgress = false;
    }
  }

  async disconnectTradesConnection(): Promise<void> {
    void super.disconnect(this.socketId);
  }

  getTradesConnection(): WebSocket | null {
    return super.getSocket(this.socketId);
  }

  private setupEventListeners() {
    const socket = this.getTradesConnection();

    if (!socket) {
      console.error(
        '[TradesWebSocketManager] Trades connection not initialized',
      );
      return;
    }

    socket.onopen = () => {
      console.log(
        `[TradesWebSocketManager] WebSocket connected --> ${this.socketId}`,
      );

      TradesWebSocketManager.setConnectionCallback(socket, this.socketId);

      super.resetReconnectAttempts(this.socketId);

      const connectionTime = super.getConnectionCreationTime(this.socketId);
      console.log('[TradesWebSocketManager] Connection time:', connectionTime);
    };

    socket.onclose = (event) => {
      console.log(
        `[TradesWebSocketManager] WebSocket disconnected --> ${this.socketId}`,
        event,
      );

      TradesWebSocketManager.setConnectionCallback(null, this.socketId);

      if (!event.wasClean) {
        super.handleReconnection(this.socketId, 'onclose');
      }
    };

    socket.onmessage = async (event: MessageEvent) => {
      try {
        const arrayBuffer = await (event.data instanceof Blob
          ? event.data.arrayBuffer()
          : event.data);

        const uint8Array = new Uint8Array(arrayBuffer);
        const decodedMessage = this.protobufDecoder(uint8Array);

        const subPath = decodedMessage?.subPath;
        const mode = decodedMessage?.mode as TradeEventMode;

        if (!subPath || !mode) {
          console.error(
            '[TradesWebSocketManager] Invalid message',
            decodedMessage,
          );
          return;
        }

        const subscriptionKeyFromMap = this.getSubscriptionKey(subPath, mode);
        const subscriptions = this.subscriptions.get(subscriptionKeyFromMap);

        if (subscriptions) {
          subscriptions.forEach((sub) => {
            try {
              sub.callback(decodedMessage);
            } catch (error: unknown) {
              console.error(
                '[TradesWebSocketManager] Error processing WebSocket message',
                error,
              );
            }
          });
        }

        this.lastOnMessageError = '';
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        console.error(
          `[TradesWebSocketManager] Error processing WebSocket message --> ${this.socketId}`,
          errorMessage,
        );
        if (this.lastOnMessageError !== errorMessage) {
          this.lastOnMessageError = errorMessage;
        }
      }
    };

    socket.onerror = (error) => {
      console.error(
        `[TradesWebSocketManager] WebSocket error --> ${this.socketId}`,
        error,
      );

      super.handleReconnection(this.socketId, 'onerror');
    };
  }

  private getSubscriptionKey(
    path: TWebsocketPath,
    mode: TradeEventMode,
  ): TSubscriptionKey {
    return `${path}-${mode}`;
  }

  private getMode(type: TradeEventTypes): TradeEventMode {
    switch (type) {
      case TradeEventTypes.GTT_ORDER:
        return TradeEventMode.GTT_ORDER;
      case TradeEventTypes.POSITION:
        return TradeEventMode.POSITION;
      case TradeEventTypes.POSITION_REFRESH:
        return TradeEventMode.POSITION_REFRESH;
      default:
        return TradeEventMode.ORDER;
    }
  }

  private subscribe(
    path: TWebsocketPath,
    callback: TradesCallback,
    type: TradeEventTypes,
  ): () => void {
    try {
      const subId = `sub-${path}-trade-${type}-${Math.random()
        .toString(8)
        .slice(2, 11)}`;

      const socket = this.getTradesConnection();

      if (!socket) {
        console.log(
          '[TradesWebSocketManager] Trades connection not initialized',
          type,
        );
        throw new Error(`Trades connection not initialized for ${type}`);
      }

      const mode = this.getMode(type);
      const subscriptionKey = this.getSubscriptionKey(path, mode);
      const existingSubscriptions =
        this.subscriptions.get(subscriptionKey) ?? [];

      if (!existingSubscriptions.length) {
        if (socket.readyState === WebSocket.OPEN) {
          const subscribeMessage = {
            mode,
            action: 'SUB',
            entities: [`${path}`],
          };
          socket.send(JSON.stringify(subscribeMessage));
        }
      }

      this.subscriptions.set(subscriptionKey, [
        ...existingSubscriptions,
        { path, callback, subId, type },
      ]);

      console.log('[TradesWebSocketManager] Subscribed to', { path, type });

      return () => {
        this.unsubscribe({ subscriptionKey, path, subId, type });
      };
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to trades',
        error,
      );
      return () => {};
    }
  }

  private unsubscribe({
    subscriptionKey,
    path,
    subId,
    type,
  }: {
    subscriptionKey: TSubscriptionKey;
    path: TWebsocketPath;
    subId: string;
    type: TradeEventTypes;
  }): void {
    try {
      const socket = this.getTradesConnection();
      const subs = this.subscriptions.get(subscriptionKey);

      if (subs) {
        const filteredSubs = subs.filter((sub) => sub.subId !== subId);

        if (filteredSubs.length === 0) {
          if (socket && socket.readyState === WebSocket.OPEN) {
            const mode = this.getMode(type);
            const unsubscribeMessage = {
              action: 'UNSUB',
              mode,
              entities: [`${path}`],
            };
            socket.send(JSON.stringify(unsubscribeMessage));
          } else {
            console.error(
              '[TradesWebSocketManager] Trades connection not initialized for unsubscribe',
              type,
            );
          }
          this.subscriptions.delete(subscriptionKey);
        } else {
          this.subscriptions.set(subscriptionKey, filteredSubs);
        }

        console.log('[TradesWebSocketManager] Unsubscribed from', {
          subscriptionKey,
          path,
          type,
        });
      } else {
        console.log('[TradesWebSocketManager] No existing subscriptions');
      }
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error unsubscribing from trades',
        error,
      );
    }
  }

  subscribeOrders(path: TWebsocketPath, callback: TradesCallback): () => void {
    try {
      return this.subscribe(path, callback, TradeEventTypes.ORDER);
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to orders',
        error,
      );
      throw error;
    }
  }

  subscribePositions(
    path: TWebsocketPath,
    callback: TradesCallback,
  ): () => void {
    try {
      return this.subscribe(path, callback, TradeEventTypes.POSITION);
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to positions',
        error,
      );
      throw error;
    }
  }

  subscribeGTTOrders(
    path: TWebsocketPath,
    callback: TradesCallback,
  ): () => void {
    try {
      return this.subscribe(path, callback, TradeEventTypes.GTT_ORDER);
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to GTT orders',
        error,
      );
      throw error;
    }
  }

  subscribePositionRefresh(
    path: TWebsocketPath,
    callback: TradesCallback,
  ): () => void {
    try {
      return this.subscribe(path, callback, TradeEventTypes.POSITION_REFRESH);
    } catch (error: unknown) {
      console.error(
        '[TradesWebSocketManager] Error subscribing to position refresh',
        error,
      );
      throw error;
    }
  }

  isSubscribed(subscriptionKey: TSubscriptionKey): boolean {
    return this.subscriptions.has(subscriptionKey);
  }
}

export default TradesWebSocketManager;
